/**
 * Browser-safe sound presets used by both the user SPA and the admin
 * SPA. Kept out of @slaplink/shared because that package's barrel pulls
 * in node:crypto via slug.ts which Vite can't bundle for the browser.
 *
 * If you change presets here, mirror the same edit in
 * services/admin/web/src/routes/SystemPage.tsx (the admin audition
 * needs the tone definitions client-side too).
 */

export interface SoundTone {
  freq: number;
  start: number;
  duration: number;
  gain?: number;
}

export interface SoundPreset {
  slug: string;
  label: string;
  tones: SoundTone[];
}

export const SOUND_PRESETS: SoundPreset[] = [
  {
    slug: 'ascending',
    label: 'Ascending two-tone (default share)',
    tones: [
      { freq: 880, start: 0, duration: 0.08 },
      { freq: 1320, start: 0.06, duration: 0.12 },
    ],
  },
  {
    slug: 'three-tone',
    label: 'Three-tone arpeggio (default notification)',
    tones: [
      { freq: 784, start: 0, duration: 0.1, gain: 0.15 },
      { freq: 988, start: 0.08, duration: 0.12, gain: 0.16 },
      { freq: 1318, start: 0.18, duration: 0.16, gain: 0.16 },
    ],
  },
  {
    slug: 'pop',
    label: 'Pop (single short blip)',
    tones: [{ freq: 1500, start: 0, duration: 0.06, gain: 0.18 }],
  },
  {
    slug: 'bell',
    label: 'Bell (long decay)',
    tones: [{ freq: 880, start: 0, duration: 0.45, gain: 0.2 }],
  },
  {
    slug: 'chime',
    label: 'Chime (C-E-G chord)',
    tones: [
      { freq: 523, start: 0, duration: 0.18, gain: 0.12 },
      { freq: 659, start: 0, duration: 0.18, gain: 0.12 },
      { freq: 784, start: 0, duration: 0.22, gain: 0.12 },
    ],
  },
  {
    slug: 'soft-ping',
    label: 'Soft ping (low + slow)',
    tones: [{ freq: 660, start: 0, duration: 0.22, gain: 0.13 }],
  },
  {
    slug: 'descending',
    label: 'Descending two-tone',
    tones: [
      { freq: 1320, start: 0, duration: 0.08 },
      { freq: 880, start: 0.06, duration: 0.12 },
    ],
  },
  { slug: 'silent', label: 'Silent (no sound on this event)', tones: [] },
];

export const SOUND_PRESET_BY_SLUG: Record<string, SoundPreset> = Object.fromEntries(
  SOUND_PRESETS.map((p) => [p.slug, p]),
);

export function resolveSoundPreset(slug: string | null | undefined): SoundPreset | null {
  if (!slug) return null;
  return SOUND_PRESET_BY_SLUG[slug] ?? null;
}
